import axios from 'axios';
import { Button, Clickable, Col, Input, Row, Text } from 'components';
import { currencies, nations, theme } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TableComponents } from 'screens/Admin/components';
import { AthleteTierItem, AthleteTierItemDefault } from './';

const API_URL = process.env.REACT_APP_API_URL;
const AthleteTier = ({ tier, setDataId, closeModal, isEditing, panelHeader }) => {
  const [tierId, setTierId] = useState(null),
    [name, setName] = useState(''),
    [defaultTierPricing, setDefaultTierPricing] = useState({
      usd: {
        amount: 0,
        athleteFee: 0,
        serviceFeePct: 0,
        serviceFee: 0
      },
      eur: {
        amount: 0,
        athleteFee: 0,
        serviceFeePct: 0,
        serviceFee: 0
      },
      jpy: {
        amount: 0,
        athleteFee: 0,
        serviceFeePct: 0,
        serviceFee: 0
      },
      mkd: {
        amount: 0,
        athleteFee: 0,
        serviceFeePct: 0,
        serviceFee: 0
      }
    }),
    [editable, setEditable] = useState(false),
    [paymentType, setPaymentType] = useState(''),
    [currencySymbols, setCurrencySymbols] = useState({}),
    [errors, setErrors] = useState([]),
    nameInputRef = useRef(null),
    dispatch = useDispatch(),
    [tierItem, setTierItem] = useState([
      { country: 'United States', amount: 0, serviceFeePct: 0, currencySymbol: '$' },
      { country: null, amount: 0, serviceFeePct: 0, currencySymbol: '$' }
    ]),
    [editError, setEditError] = useState([]),
    isDefaultTierItem = tier?.default || !!tier;
  let allEditErrors = [...editError];

  useEffect(() => {
    const currencyMap = {};

    Object.keys(defaultTierPricing).forEach((currency) => {
      currencyMap[currency] = currencies.find((x) => x.value.toLowerCase() === currency).symbol;
    });
    if (panelHeader === 'Shoutout from an Athlete') {
      setPaymentType('shoutoutFromAthlete');
    }

    setCurrencySymbols(currencyMap);
    if (tier) {
      setTierId(tier._id || null);

      if (tier.paymentType !== null) {
        setPaymentType(tier.paymentType);
      }

      if (tier.name !== null) {
        setName(tier.name);
      }

      let basePricing = {};

      Object.keys(defaultTierPricing).forEach((currency, i) => {
        basePricing[currency] = {
          amount: tier.pricing[i].amount || 0,
          serviceFeePct: tier.pricing[i].serviceFeePct || 0,
          serviceFee: 0,
          athleteFee: 0
        };

        basePricing[currency].serviceFee = (
          (basePricing[currency].amount * basePricing[currency].serviceFeePct) /
          100
        ).toFixed(2);
        basePricing[currency].athleteFee = (
          basePricing[currency].amount - basePricing[currency].serviceFee
        ).toFixed(2);
        basePricing[currency].amount = basePricing[currency].amount.toFixed(2);
      });

      setDefaultTierPricing(basePricing);
    } else {
      setEditable(true);
    }
  }, []);

  const addNewItem = () => {
    setTierItem([...tierItem, { country: null, amount: 0, serviceFeePct: 0, currencySymbol: '$' }]);
  };

  /**
   * Resolve axios exceptions from adding / updating athlete tier endpoints
   */
  function handleAxiosError(e) {
    if (e.response?.data?.input) {
      let newErr = [...errors];

      newErr.push({ input: e.response.data.input, err: e.response.data.err });
      nameInputRef.current.focus();
      setErrors(newErr);
    } else {
      if (e.response?.data?.includes('Value is more than 10000')) {
        dispatch({
          type: 'HANDLE_RES',
          payload: { err: 'Value is more than 10,000' }
        });
        return;
      }
      const message = e.response?.data?.err
        ? e.response.data.err
        : 'Something went wrong.\nPlease try again later.';

      dispatch({
        type: 'HANDLE_RES',
        payload: { err: message }
      });
    }
  }

  /**
   * Input change handler
   * @param {string} currency currency code where changes should reflect
   * @param {string} category price | fee
   * @param {string | number} value the amount / percentage change from input
   * @returns {boolean}
   */
  function handleChange(currency, category, value) {
    let processedValue = value || '';

    if (!processedValue) {
      processedValue = 0;
    }

    let parsedValue = parseFloat(processedValue);

    if (isNaN(parsedValue)) {
      return false;
    }

    let currentPricing = { ...defaultTierPricing };

    if (category === 'price') {
      if (currentPricing[currency].amount === parsedValue) {
        return true;
      }

      currentPricing[currency].amount = parsedValue;
    }

    if (category === 'fee') {
      parsedValue = Math.floor(parsedValue);

      if (currentPricing[currency].serviceFeePct === parsedValue) {
        return true;
      }

      currentPricing[currency].serviceFeePct = parsedValue;
    }

    // set value in state but prevent it later from saving in validation
    if (currentPricing[currency].serviceFeePct <= 100) {
      currentPricing[currency].serviceFee = (
        (currentPricing[currency].amount * currentPricing[currency].serviceFeePct) /
        100
      ).toFixed(2);
      currentPricing[currency].athleteFee = (
        currentPricing[currency].amount - currentPricing[currency].serviceFee
      ).toFixed(2);

      if (category === 'price') {
        currentPricing[currency].amount = currentPricing[currency].amount.toFixed(2);
      }
    }

    setDefaultTierPricing(currentPricing);

    return true;
  }

  /**
   * Error handler
   * @param {string} currency currency code where changes should reflect
   * @param {string} field thr field to check
   * @param {string | number} value the amount / percentage change from input
   */
  function handleError(currency, field, value) {
    let newErrs = [...errors];

    if (field === 'name') {
      newErrs = newErrs.filter((x) => x.input !== 'name');

      if (!value.trim()) {
        newErrs.push({ input: 'name', err: 'Tier name is required.' });
      }
    }

    if (field === 'fee') {
      value = parseInt(value.replace('%', ''));

      if (isNaN(value)) {
        value = 0;
      }

      newErrs = newErrs.filter((x) => x.input !== 'fee' && x.currency !== currency);

      if (value > 100) {
        newErrs.push({
          input: 'fee',
          currency: currency,
          err: 'Service fee should not be greater than 100%.'
        });
      }
    }

    setErrors(newErrs);
  }
  /**
   * handle change for Add tier functionality
   * @param {string} field thr field to check
   * @param {string} value the amount / percentage change from input
   * @param {string | number} index the index of the input field
   */
  const onEditTiers = (field, value, index) => {
    handleEditError(field, value, index);
    const updatedTierItem = [...tierItem];
    value = value || '';
    if (field === 'country') {
      const currencySymbol = nations.find((nation) => nation.value === value).currencySymbol;
      updatedTierItem[index] = {
        ...updatedTierItem[index],
        [field]: value,
        currencySymbol
      };
    } else {
      updatedTierItem[index] = {
        ...updatedTierItem[index],
        [field]: value
      };
    }

    setTierItem(updatedTierItem);
  };

  /**
   * handle error for Add tier functionality
   * @param {string} field
   * @param {string} value
   * @param {string | number} index
   */
  const handleEditError = (field, value, index) => {
    const updatedErrors = [...editError];

    if (value === null || value === '') {
      //error checking on submit of request
      allEditErrors[index] = {
        ...allEditErrors[index],
        [field]: 'Required'
      };
      setEditError(allEditErrors);
      return allEditErrors;
    }

    if (field === 'amount') {
      if (value > 10000) {
        updatedErrors[index] = {
          ...updatedErrors[index],
          amount: 'Price should not be greater than 10,000'
        };
        setEditError(updatedErrors);
      } else {
        if (updatedErrors[index]) {
          delete updatedErrors[index].amount;
          setEditError(updatedErrors);
        }
      }
    }

    if (field === 'serviceFeePct') {
      if (value > 100 || value < 0) {
        updatedErrors[index] = {
          ...updatedErrors[index],
          serviceFeePct: 'Service fee should not be greater than 100%.'
        };
        setEditError(updatedErrors);
      } else {
        if (updatedErrors[index]) {
          delete updatedErrors[index].serviceFeePct;
          setEditError(updatedErrors);
        }
      }
    }

    if (field === 'country') {
      if (isDuplicateCountry(value, index)) {
        updatedErrors[index] = { ...updatedErrors[index], country: 'Country already exist' };
        setEditError(updatedErrors);
      } else {
        if (updatedErrors[index]) {
          delete updatedErrors[index].country;
          setEditError(updatedErrors);
        }
      }
    }
  };

  const hasErrors = (errorArray) => {
    if (errorArray && errorArray.length > 0) {
      return errorArray.some((error) => error && Object.keys(error).length !== 0);
    }
    return false;
  };

  const isDuplicateCountry = (country, index) => {
    return tierItem.some((item, i) => item.country === country && i !== index);
  };

  const onDeleteTier = (index) => {
    setTierItem(tierItem.filter((_, i) => i !== index));
    setEditError(editError.filter((_, i) => i !== index));
  };

  /**
   * get the tier value of each tier
   * this will be updated when the API for addTier is updated
   * @param {string} country
   * @param {string} field
   * @returns {string}
   */
  const getValue = (country, field) => {
    const item = tierItem.find((item) => item.country === country);

    if (field === 'athleteFee') {
      const athleteFee = item?.amount * (100 - item?.serviceFeePct) * 0.01;
      return item ? athleteFee : null;
    }

    return item ? item[field] : null;
  };

  /**
   * Routine for Adding athlete tier
   * @returns {Promise}
   */
  async function submit() {
    tierItem.forEach((item, index) => {
      handleEditError('amount', item.amount, index);
      handleEditError('serviceFeePct', item.serviceFeePct, index);
      handleEditError('country', item.country, index);
    });
    handleError('', 'name', name);

    setEditError(allEditErrors);

    if (hasErrors(allEditErrors) || hasErrors(editError)) {
      return;
    }
    dispatch({
      type: 'LOADING_BUTTON',
      payload: 'addAthleteTier'
    });

    if (errors.length) {
      dispatch({
        type: 'LOADING_BUTTON',
        payload: null
      });

      return;
    }

    const data = {
      name,
      paymentType,
      pricingUsdAmount: getValue('United States', 'amount'),
      pricingUsdAthleteFee: getValue('United States', 'athleteFee'),
      pricingUsdServiceFeePct: getValue('United States', 'serviceFeePct'),
      pricingEurAmount: getValue('Italy', 'amount'),
      pricingEurAthleteFee: getValue('Italy', 'athleteFee'),
      pricingEurServiceFeePct: getValue('Italy', 'serviceFeePct'),
      pricingJpyAmount: getValue('Japan', 'amount'),
      pricingJpyAthleteFee: getValue('Japan', 'athleteFee'),
      pricingJpyServiceFeePct: getValue('Japan', 'serviceFeePct'),
      pricingMkdAmount: getValue('Republic of North Macedonia', 'amount'),
      pricingMkdAthleteFee: getValue('Republic of North Macedonia', 'athleteFee'),
      pricingMkdServiceFeePct: getValue('Republic of North Macedonia', 'serviceFeePct')
    };

    let res;
    try {
      res = await axios.post(`${API_URL}/admin/addAthleteTier`, data);
    } catch (e) {
      handleAxiosError(e);
    }

    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    dispatch({
      type: 'ADMIN_ADD_ATHLETE_TIER',
      payload: res.data
    });

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: `Athlete Tier added.` }
    });

    setDataId(res._id);
    setTimeout(closeModal, 300);
  }

  /**
   * Routine for Editing athlete tier
   * @returns {Promise}
   */
  async function edit() {
    if (errors.length) {
      dispatch({
        type: 'LOADING_BUTTON',
        payload: null
      });

      return;
    }

    dispatch({
      type: 'LOADING_BUTTON',
      payload: 'updateAthleteTier'
    });

    const data = {
      pricingUsdAmount: defaultTierPricing.usd.amount,
      pricingUsdAthleteFee: defaultTierPricing.usd.athleteFee,
      pricingUsdServiceFeePct: defaultTierPricing.usd.serviceFeePct,
      pricingEurAmount: defaultTierPricing.eur.amount,
      pricingEurAthleteFee: defaultTierPricing.eur.athleteFee,
      pricingEurServiceFeePct: defaultTierPricing.eur.serviceFeePct,
      pricingJpyAmount: defaultTierPricing.jpy.amount,
      pricingJpyAthleteFee: defaultTierPricing.jpy.athleteFee,
      pricingJpyServiceFeePct: defaultTierPricing.jpy.serviceFeePct,
      pricingMkdAmount: defaultTierPricing.mkd.amount,
      pricingMkdAthleteFee: defaultTierPricing.mkd.athleteFee,
      pricingMkdServiceFeePct: defaultTierPricing.mkd.serviceFeePct
    };

    if (name || name === '') {
      data.name = name;
    }

    let res;

    try {
      res = await axios.put(`${API_URL}/admin/updateAthleteTier/${tierId}`, data);
    } catch (e) {
      handleAxiosError(e);
    }

    dispatch({
      type: 'LOADING_BUTTON',
      payload: null
    });

    if (!res) {
      return;
    }

    dispatch({
      type: 'ADMIN_EDIT_ATHLETE_TIER',
      payload: res.data
    });
    dispatch({
      type: 'HANDLE_RES',
      payload: { res: 'Athlete Tier updated.' }
    });
    setTimeout(closeModal, 300);
  }

  return (
    <Col start between ht="calc(100% - 55px)" pad="0 24px">
      <Col wid="100%" noFlex>
        <Row marg="24px 0 0" pos="relative">
          <Input
            title="Tier"
            wid="100%"
            inputRef={nameInputRef}
            placeholder="Name..."
            defaultValue={name}
            disabled={tier?.default}
            bg={tier?.default && 'rgba(121, 137, 160, 0.2)'}
            onChange={(e) => {
              setName(e.target.value);
              handleError('', 'name', e.target.value);
            }}
            error={errors.find((x) => x.input === 'name')?.err}
          />
        </Row>
      </Col>
      <TableComponents.List
        Header={() => (
          <React.Fragment>
            <TableComponents.Th noBorder>
              <Text b2 med>
                Country
              </Text>
            </TableComponents.Th>
            <TableComponents.Th noBorder>
              <Text b2 med>
                Price
              </Text>
            </TableComponents.Th>
            <TableComponents.Th noBorder>
              <Text b2 med>
                Service Fee(%)
              </Text>
            </TableComponents.Th>
            <TableComponents.Th noBorder>
              <Text b2 med>
                Service Fee
              </Text>
            </TableComponents.Th>
            <TableComponents.Th noBorder>
              <Text b2 med>
                Athlete's Fee
              </Text>
            </TableComponents.Th>
            <TableComponents.Th noBorder last>
              <Text b2 med></Text>
            </TableComponents.Th>
          </React.Fragment>
        )}
        Items={
          isDefaultTierItem
            ? () =>
                tier.pricing.map((item, i) => {
                  return (
                    <AthleteTierItemDefault
                      defaultTier={isDefaultTierItem}
                      key={i}
                      tierPricing={defaultTierPricing}
                      country={item.country}
                      currency={item.currency}
                      currencySymbols={currencySymbols}
                      errors={errors}
                      setErrors={setErrors}
                      handleError={handleError}
                      handleChange={handleChange}
                    />
                  );
                })
            : () =>
                tierItem.map((item, i) => {
                  return (
                    <AthleteTierItem
                      onDeleteTier={onDeleteTier}
                      index={i}
                      defaultTier={tier?.default}
                      key={i}
                      item={item}
                      handleChange={onEditTiers}
                      error={editError}
                    />
                  );
                })
        }
        Footer={
          !isDefaultTierItem
            ? () => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: '20px',
                      marginRight: '40px',
                      marginLeft: 'auto'
                    }}
                  >
                    <Clickable onClick={addNewItem}>
                      <Text col="red" bold>
                        Add country
                      </Text>
                    </Clickable>
                  </div>
                );
              }
            : null
        }
      />

      <Row pad="54px 0 24px">
        <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

        <Button
          big
          onClick={isEditing ? edit : submit}
          text="Save"
          bg={theme.SECONDARY}
          action="addAthleteTier"
        />
      </Row>
    </Col>
  );
};

export default AthleteTier;
